import { FormControl, FormErrorMessage, FormLabel, IconButton, Input, InputGroup, InputRightElement } from '@chakra-ui/react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';

import { eventValue } from '@app/utils/event-value';
import { getPropValue } from '@app/utils/get-property';

import { Dropdown } from './dropdown';
import { useSelectKeyboard } from './hooks/useSelectKeyboard';
import { FieldComponentProps } from './types';

export type AutocompleteFieldProps = Omit<FieldComponentProps<EntityBase>, 'value'> & {
  value?: string | EntityBase;
  name?: string;
  valueProperty?: string | string[];
  searchKey?: string;
  options?: EntityBase[];
  onFetchOptions?: (
    name: string,
    query: Record<string, string>
  ) => Promise<EntityBase[]>;
  multiple?: boolean;
  clear?: boolean;
  renderOption?: (option: EntityBase) => ReactNode;
  onChangeText?: (text: string) => void;
};

export const AutocompleteField = (props: AutocompleteFieldProps) => {
  const {
    id,
    name,
    label,
    value,
    error,
    helperText,
    searchKey = 'search',
    valueProperty = 'name',
    renderOption,
    required,
    disabled,
    multiple,
    onChange,
    onFetchOptions,
    onChangeText,
    clear = true,
    readOnly = false,
  } = props;
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<EntityBase[]>(
    props.options || []
  );
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const fetchOptionsTimerRef = useRef<any>();

  useEffect(() => {
    setInputValue(getPropValue(value as EntityBase, valueProperty) || value?.toString() || '');
  }, [value, valueProperty]);

  const onChangeInput = (text: string) => {
    if (readOnly) {return;}
    setInputValue(text);
    onChangeText?.(text);
    if (text.length < 4) {
      return;
    }
    if (!onFetchOptions) {
      return;
    }
    setLoading(true);
    clearTimeout(fetchOptionsTimerRef.current);
    fetchOptionsTimerRef.current = setTimeout(() => {
      onFetchOptions(name || '', { [searchKey]: text })
        .then((options) => {
          setOptions(options || []);

          if (options.length === 1 && getPropValue(options[0], valueProperty) === text) {
            onChange(options[0]);
          }
        })
        .finally(() => setLoading(false));
    }, 800);
  };

  const handleClickOutside = () => {
    document.removeEventListener('mousedown', handleClickOutside);
    setOpen(false);
  };

  const handleMouseLeave = () => {
    document.addEventListener('mousedown', handleClickOutside);
  };

  const handleMouseEnter = () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };


  const handleOptionSelect = (option: EntityBase) => () => {
    setOpen(false);
    onChange?.(option);
    onChangeText?.(getPropValue(option as EntityBase, valueProperty) || option?.toString() || '');
    setInputValue(getPropValue(option as EntityBase, valueProperty) || option?.toString() || '');
  };

  const handleClear = () => {
    setInputValue('');
    onChange?.(undefined);
  };
  const { marker, onKeyDown } = useSelectKeyboard({
    options,
    onChange,
    setOpen,
  });

  return (<InputGroup>
    <FormControl 
      variant="floating" 
      id={id} 
      isRequired={required}
      isInvalid={error}
      isDisabled={disabled}
      ref={fieldRef}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <InputRightElement pr={0}>
        {!multiple && !disabled && <IconButton icon={<MdClose />} aria-label="clear" 
          backgroundColor="transparent" 
          borderRadius="50%"
          size="xs"
          onClick={handleClear}
        />}
      </InputRightElement>
      <Dropdown 
        fieldRef={fieldRef}
        open={open}
        setOpen={setOpen}
      >
        {!loading && options.length === 0 
          && inputValue.length === 0 
          && <Dropdown.Label label="Начните ввод" />}
        {!loading && options.length === 0 
          && inputValue.length !== 0 
          && <Dropdown.Label label="Ничего не найдено"/>}
        {loading && <Dropdown.Loader />}
        {(options || []).map((item, index) => {
          if (renderOption) {
            return <Dropdown.Option 
              key={item.id} 
              hovered={marker === index}
              onClick={handleOptionSelect(item)}
            >{renderOption(item)}</Dropdown.Option>;
          }
          return <Dropdown.Option
            key={item.id}
            hovered={marker === index}
            onClick={handleOptionSelect(item)}
          >
              {getPropValue(item, valueProperty)} 
            </Dropdown.Option>; 
        })}
      </Dropdown>
      {!multiple && <Input 
        placeholder={label}
        value={inputValue}
        contentEditable={false}
        onClick={() => !readOnly && setOpen(true)}
        onChange={eventValue(onChangeInput)}
        onKeyDown={onKeyDown}
        paddingEnd={8}
        readOnly={readOnly}
      />}
      <FormLabel textColor="dark.300">{label}</FormLabel>
      {error && <FormErrorMessage>{helperText}</FormErrorMessage>}
    </FormControl>
  </InputGroup>);
};

export default AutocompleteField;